import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchLicenses,
  createLicense,
  deleteLicense,
  type LicenseDto,
} from '../../api/libraryApi';
import { ApiError } from '../../services/apiClient';

export function LicensesSettingsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const { data: licenses, isLoading } = useQuery({
    queryKey: ['admin-licenses'],
    queryFn: fetchLicenses,
  });

  const createMutation = useMutation({
    mutationFn: (value: string) => createLicense(value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      setName('');
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('admin.licenses.createFailed', 'Failed to create license');
      toast({ title: message, status: 'error', duration: 4000 });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (licenseId: string) => deleteLicense(licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('admin.licenses.deleteFailed', 'Failed to delete license');
      toast({ title: message, status: 'error', duration: 4000 });
    },
  });

  function handleAdd() {
    const trimmed = name.trim();
    if (!trimmed) return;
    createMutation.mutate(trimmed);
  }

  function deleteReason(license: LicenseDto): string | null {
    if (license.isSystem) return t('admin.licenses.systemCannotDelete');
    if (license.inUse) return t('admin.licenses.deleteInUse');
    return null;
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="licenses-settings-page">
      <Heading size="md" mb={4} color={textColor}>{t('admin.licenses.title')}</Heading>

      {/* Add license */}
      <Flex gap={3} mb={6} maxW="480px">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          placeholder={t('admin.licenses.name')}
          data-testid="license-name-input"
        />
        <Button
          colorScheme="brand"
          onClick={handleAdd}
          isDisabled={!name.trim() || createMutation.isPending}
          isLoading={createMutation.isPending}
          data-testid="btn-add-license"
        >
          {t('admin.licenses.add')}
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center" p={8}><Spinner size="xl" /></Flex>
      ) : !licenses || licenses.length === 0 ? (
        <Text color={secondaryColor} data-testid="licenses-empty">{t('admin.licenses.empty')}</Text>
      ) : (
        <Table variant="simple" size="sm" data-testid="licenses-table">
          <Thead>
            <Tr>
              <Th color={secondaryColor} borderColor={borderColor}>{t('admin.licenses.name')}</Th>
              <Th color={secondaryColor} borderColor={borderColor}>{t('admin.common.status')}</Th>
              <Th color={secondaryColor} borderColor={borderColor} textAlign="right">{t('admin.licenses.delete')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {licenses.map((license) => {
              const reason = deleteReason(license);
              return (
                <Tr key={license.id} data-testid={`license-row-${license.id}`}>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm" fontWeight="600">{license.name}</Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Flex gap={1} flexWrap="wrap">
                      {license.isSystem && (
                        <Badge colorScheme="blue" borderRadius="8px" px="8px" fontSize="xs">
                          {t('admin.licenses.system', 'System')}
                        </Badge>
                      )}
                      {license.inUse && (
                        <Badge colorScheme="orange" borderRadius="8px" px="8px" fontSize="xs">
                          {t('admin.licenses.inUse')}
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  <Td borderColor={borderColor} textAlign="right">
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="outline"
                      isDisabled={!!reason || deleteMutation.isPending}
                      title={reason ?? undefined}
                      onClick={() => deleteMutation.mutate(license.id)}
                      data-testid={`btn-delete-license-${license.id}`}
                    >
                      {t('admin.licenses.delete')}
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
